import {
  closeAuthorizingModal,
  showAuthorizingModal,
  showOfflineGiftCardActivationModal,
} from 'src/brm/actions/authorize/authorize-online-items';
import { EmeraldError } from 'src/common/api/emerald-error';
import { ActivationStatus } from 'src/common/enumerations/activation-status';
import { AuthorizationStatus } from 'src/common/enumerations/authorization-status';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import { getValueAsString } from 'src/common/helpers/string-helper';
import { createSpan } from 'src/common/helpers/tracing-helper';
import { store } from 'src/common/redux/configure-store';
import { PaymentMessage, ReceiptSection, Status } from 'src/devices/payment/dtos/eps-data';
import {
  activateOnlineItem,
  authorizeOnlineItemDto,
  beginPaymentSession,
  endPaymentSession,
  inPaymentSession,
} from 'src/devices/payment/pinpad-operations';
import { addLineItemToTransaction } from 'src/home-page/business-logic/sale-mode-router';
import { dataAdd } from 'src/home-page/business-logic/service/data-add';
import { ILineItemAddData } from 'src/items-list/business-logic/line-item-add';
import { reloadOnlineItem } from './../devices/payment/pinpad-operations';
import { OnlineItemServiceType } from './dto/CartItemActivation';

export const performOnlineItemAuthorizationItemSale = async (
  lineItemData: ILineItemAddData,
  externalId?: string,
): Promise<void> => {
  const span = createSpan('perform-online-item-authorization');
  let authorizationStatus = AuthorizationStatus.Decline;
  let activationAuthCode: string | undefined = 'String';
  showAuthorizingModal();
  if (!inPaymentSession()) {
    await beginPaymentSession();
  }
  let decodedExternalId: string;
  let authorizationDateTime: string | undefined = '';
  let activateOnlineItemData: PaymentMessage = {};
  const transactionId = store.getState().transaction.transactionId;
  if (externalId) {
    decodedExternalId = externalId;
  } else {
    decodedExternalId = await dataAdd(transactionId, {
      itemId: lineItemData.inputData.itemId,
    })
      .then(result => {
        return result.onlineItemExternalId ?? '';
      })
      .catch(() => {
        closeAuthorizingModal();
        throw new EmeraldError('CannotDecodeInBarcodeItemId');
      });
  }
  try {
    const newPrice = lineItemData.lineItem.newPrice;
    const priceAdd = newPrice
      ? lineItemData.lineItem.newPrice
      : lineItemData.itemLookup.regularSalesUnitPrice?.price.value;
    const authorizeOnlineItem =
      lineItemData.lineItem.additionalData?.onlineItem?.serviceType === OnlineItemServiceType.Reload
        ? reloadOnlineItem
        : activateOnlineItem;

    const authorizeOnlineItemData: authorizeOnlineItemDto = {
      price: getValueAsString(priceAdd),
      externalId: decodedExternalId,
      entryMethod: EntryMethod.Empty,
    };

    if (decodedExternalId) {
      authorizeOnlineItemData.entryMethod = EntryMethod.Barcode;
    }

    if (lineItemData.lineItem.item.id) {
      authorizeOnlineItemData.upc = lineItemData.itemLookup.itemId;
    }

    activateOnlineItemData = await authorizeOnlineItem(
      authorizeOnlineItemData,
      span.spanContext(),
    ).finally(() => endPaymentSession());
    if (activateOnlineItemData.status === Status.Success) {
      authorizationDateTime = new Date().toISOString();
      authorizationStatus = AuthorizationStatus.Success;
      if (activateOnlineItemData?.paymentDetails?.offline) {
        authorizationDateTime = undefined;
        authorizationStatus = AuthorizationStatus.Offline;
        showOfflineGiftCardActivationModal();
      }
    }
    activationAuthCode = activateOnlineItemData.paymentDetails?.authCode;
  } catch (error) {
    authorizationStatus = AuthorizationStatus.Decline;
  }
  let receiptBodyLine: string[] = [];
  if (activateOnlineItemData.receipts) {
    receiptBodyLine = activateOnlineItemData.receipts.filter(
      ReceiptData => ReceiptData.receiptSection === ReceiptSection.Body,
    )[0].receiptLines;
  }
  closeAuthorizingModal();
  const body: ILineItemAddData = {
    lineItem: {
      item: {
        id: lineItemData.itemLookup.itemId ?? '',
        entryMethod: lineItemData.lineItem.item.entryMethod,
      },
      quantity: lineItemData.lineItem.quantity,
      uomCode: lineItemData.lineItem.uomCode,
      withData: false,
      additionalData: {
        onlineItem: {
          authorizationExternalId: decodedExternalId,
          receiptText: receiptBodyLine,
          serviceType: lineItemData.lineItem.additionalData?.onlineItem?.serviceType,
          externalIdEntryMethod:
            lineItemData.lineItem.additionalData?.onlineItem?.externalIdEntryMethod,
        },
        activation: {
          status: ActivationStatus.Activated,
          activationData: {
            authorizationStatus: authorizationStatus,
            authorizationCode: activationAuthCode,
            referenceNumber: activateOnlineItemData.paymentDetails?.paymentTranID,
            authorizationDateTime: authorizationDateTime,
          },
        },
      },
      modifiers: lineItemData.lineItem.modifiers,
      priceModifierType: lineItemData.lineItem.priceModifierType,
      newPrice: lineItemData.lineItem.newPrice,
    },
    inputData: {
      itemId: lineItemData.itemLookup.itemId ?? '',
    },
    itemLookup: lineItemData.itemLookup,
  };

  return addLineItemToTransaction(body);
};
